import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { getCourseThreads } from '../../api/threads';
import { getLessons } from '../../api/lessons';
import { unwrapList } from '../../utils/api';
import { failure, failureText } from '../teacher/apiError';
import { usePolling } from '../../hooks/usePolling';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import SearchInput from '../../components/SearchInput/SearchInput';
import FilterSheet from '../../components/FilterSheet/FilterSheet';
import PageHead from '../../components/PageHead/PageHead';
import ThreadList from '../../components/QA/ThreadList';
import ThreadDetail from '../../components/QA/ThreadDetail';
import Dropdown from '../../components/Dropdown/Dropdown';
import { useI18n } from '../../i18n/I18nContext';
import { formatTime } from '../../utils/datetime';
import './CourseQA.css';

// Every thread of the course in one request (GET /courses/:courseId/threads),
// each tagged with its lesson. All statuses come back: sorting, filtering and
// search happen client-side, so changing a filter needs no new request. The
// lessons are still read once, for the lesson filter and the no-lessons state.
async function fetchAllCourseThreads(courseId) {
  const res = await getCourseThreads(courseId);
  return unwrapList(res).map((th) => ({ ...th, lessonName: th.lessonTitle }));
}

function CourseQA() {
  const { t } = useI18n();
  const { courseId } = useParams();
  const courseTitle = useCourseTitle(courseId);
  // A sub-page of the course workspace: a back button and the course's name over the title
  const head = (actions) => (
    <PageHead
      back={{ to: `/courses/edit/${courseId}`, label: t('qaThreads.backToCourse') }}
      eyebrow={courseTitle || t('qaThreads.parent')}
      title={t('qaThreads.pageTitle')}
      actions={actions}
    />
  );

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [lessonsError, setLessonsError] = useState(null);

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [sortBy, setSortBy] = useState('latest');
  const [filterStatus, setFilterStatus] = useState('all');
  const [lessonFilter, setLessonFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch the course's lessons once (and again on "retry").
  const loadLessons = useCallback(() => {
    let cancelled = false;
    setLessonsLoading(true);
    getLessons(courseId, { limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setLessons(unwrapList(res));
        setLessonsError(null);
      })
      .catch((err) => {
        if (!cancelled) setLessonsError(failure(err, t('qaThreads.lessonsLoadFailed')));
      })
      .finally(() => { if (!cancelled) setLessonsLoading(false); });
    return () => { cancelled = true; };
  }, [courseId, t]);

  useEffect(() => loadLessons(), [loadLessons]);

  const hasLessons = lessons.length > 0;
  const fetchThreads = useCallback(
    async ({ silent = false } = {}) => {
      // A course without lessons has no questions: its own empty state shows instead.
      if (!hasLessons) return;
      try {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        const all = await fetchAllCourseThreads(courseId);
        setThreads(all);
        setLastUpdated(new Date());
        setError(null);
      } catch (err) {
        // The API client normalizes a 403 to { message, code: 403, ... } —
        // no `.response` on it — so check `code`, not `response.status`.
        setError((err?.code === 403 || err?.response?.status === 403)
          ? { forbidden: true, detail: t('qaThreads.accessDenied') }
          : failure(err, t('qaThreads.errorLoad')));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [courseId, hasLessons, t],
  );

  useEffect(() => {
    if (!lessonsLoading) fetchThreads();
  }, [lessonsLoading, fetchThreads]);

  // Background poll, paused while tab is hidden — see hooks/usePolling.js
  usePolling(() => fetchThreads({ silent: true }), 30_000);

  const filteredThreads = useMemo(() => {
    let list = threads;
    if (lessonFilter !== 'all') list = list.filter((th) => th.lessonId === lessonFilter);
    if (filterStatus === 'answered') list = list.filter((th) => th.status === 'ANSWERED');
    else if (filterStatus === 'unanswered') list = list.filter((th) => th.status === 'OPEN');

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((th) => (th.body || '').toLowerCase().includes(q)
        || (th.authorName || '').toLowerCase().includes(q));
    }

    const askedTime = (th) => new Date(th.askedAt || th.createdAt).getTime();
    const openRank = (th) => (th.status === 'OPEN' ? 0 : th.status === 'CLOSED' ? 1 : 2);

    return [...list].sort((a, b) => {
      if (sortBy === 'popular') return (b.replies?.length || 0) - (a.replies?.length || 0) || askedTime(b) - askedTime(a);
      if (sortBy === 'unanswered') return openRank(a) - openRank(b) || askedTime(b) - askedTime(a);
      return askedTime(b) - askedTime(a); // latest
    });
  }, [threads, lessonFilter, filterStatus, searchQuery, sortBy]);

  const hasFilters = Boolean(searchQuery.trim()) || lessonFilter !== 'all' || filterStatus !== 'all';
  const clearFilters = () => {
    setSearchQuery('');
    setLessonFilter('all');
    setFilterStatus('all');
  };
  // What the "Filtrlar" sheet holds on a phone: the lesson, the order and the status, off their defaults
  const sheetFilterCount = (lessonFilter !== 'all' ? 1 : 0) + (sortBy !== 'latest' ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0);
  const resetSheetFilters = () => {
    setLessonFilter('all');
    setSortBy('latest');
    setFilterStatus('all');
  };

  const handleSelectThread = (thread) => {
    setSelectedThread(thread);
  };

  const handleBackToList = () => {
    setSelectedThread(null);
    fetchThreads({ silent: true });
  };

  // Show thread detail if selected
  if (selectedThread) {
    return (
      <ThreadDetail
        thread={selectedThread}
        courseId={courseId}
        courseTitle={courseTitle}
        lessonName={selectedThread.lessonName}
        onBack={handleBackToList}
      />
    );
  }

  if (lessonsLoading) {
    return (
      <div className="course-qa">
        {head()}
        <LoadingState message={t('qaThreads.loadingLessons')} skeleton="list" rows={5} />
      </div>
    );
  }

  if (lessons.length === 0) {
    return (
      <div className="course-qa">
        {head()}
        {lessonsError ? (
          <PageEmptyState variant="error" title={lessonsError.title} description={lessonsError.detail} onRetry={loadLessons} />
        ) : (
          <PageEmptyState
            variant="docs"
            title={t('qaThreads.noLessonsTitle')}
            description={t('qaThreads.noLessonsDesc')}
          />
        )}
      </div>
    );
  }

  // A failed/denied load has no reliable thread data at all — showing the
  // (dead) search/filter controls plus a "No questions yet" empty state
  // alongside the error banner would contradict it (implies the page works
  // and the true count is zero). Render a single dedicated error state
  // instead, with no controls, whenever there is an error and nothing to
  // show. A transient error during a background refresh (stale threads
  // already on screen) still falls through to the normal list below.
  const loadFailed = error && threads.length === 0 && !loading;

  return (
    <div className="course-qa">
      {head(!loadFailed && (
        <>
          {lastUpdated && (
            <span className="cqa-updated">
              {t('qaThreads.updated', { time: formatTime(lastUpdated) })}
            </span>
          )}
          <button
            type="button"
            onClick={() => fetchThreads({ silent: true })}
            disabled={refreshing}
            className={`ds-btn ds-btn-secondary${refreshing ? ' is-loading' : ''}`}
            title={t('qaThreads.refreshTitle')}
            aria-busy={refreshing || undefined}
          >
            <RefreshCw aria-hidden="true" />
            {t('qaThreads.refresh')}
          </button>
        </>
      ))}

      {loading && threads.length === 0 ? (
        <LoadingState message={t('qaThreads.loading')} skeleton="list" rows={5} />
      ) : loadFailed && error.forbidden ? (
        <PageEmptyState variant="forbidden" description={error.detail} />
      ) : loadFailed ? (
        <PageEmptyState
          variant="error"
          title={error.title}
          description={error.detail}
          onRetry={() => fetchThreads()}
        />
      ) : (
        <>
          {error && (
            <div className="ui-alert cqa-error" role="alert">
              <AlertCircle aria-hidden="true" />
              <p>{failureText(error)}</p>
            </div>
          )}

          {/* Filters and Search: the three selects fold into one "Filtrlar" button on a phone */}
          <div className="cqa-controls">
            <SearchInput
              className="cqa-search"
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('qaThreads.searchPlaceholder')}
            />

            <FilterSheet count={sheetFilterCount} onClear={resetSheetFilters}>
              {/* The names show only inside the phone sheet, where a bare value would not say what it filters */}
              {lessons.length > 1 && (
                <div className="cqa-filter cqa-filter--lesson">
                  <span className="cqa-filter__label ui-label">{t('qaThreads.filterLessonLabel')}</span>
                  <Dropdown
                    size="sm"
                    value={lessonFilter}
                    onChange={setLessonFilter}
                    ariaLabel={t('qaThreads.lessonFilterAria')}
                    options={[
                      { value: 'all', label: t('qaThreads.lessonFilterAll') },
                      ...lessons.map((l) => ({ value: l.id, label: l.title })),
                    ]}
                  />
                </div>
              )}

              <div className="cqa-filter">
                <span className="cqa-filter__label ui-label">{t('qaThreads.filterSortLabel')}</span>
                <Dropdown
                  size="sm"
                  value={sortBy}
                  onChange={setSortBy}
                  ariaLabel={t('qaThreads.sortAria')}
                  options={[
                    { value: 'latest', label: t('qaThreads.sortLatest') },
                    { value: 'popular', label: t('qaThreads.sortPopular') },
                    { value: 'unanswered', label: t('qaThreads.sortUnanswered') },
                  ]}
                />
              </div>

              <div className="cqa-filter">
                <span className="cqa-filter__label ui-label">{t('qaThreads.filterStatusLabel')}</span>
                <Dropdown
                  size="sm"
                  value={filterStatus}
                  onChange={setFilterStatus}
                  ariaLabel={t('qaThreads.statusFilterAria')}
                  options={[
                    { value: 'all', label: t('qaThreads.filterAllThreads') },
                    { value: 'answered', label: t('qaThreads.filterAnswered') },
                    { value: 'unanswered', label: t('qaThreads.filterUnanswered') },
                  ]}
                />
              </div>
            </FilterSheet>
          </div>

          {/* Threads List */}
          {filteredThreads.length === 0 ? (
            hasFilters ? (
              <PageEmptyState
                variant="search"
                title={t('qaThreads.noMatchTitle')}
                description={t('qaThreads.noMatchDesc')}
                actionLabel={t('qaThreads.clearFilters')}
                onAction={clearFilters}
              />
            ) : (
              <PageEmptyState
                variant="inbox"
                title={t('qaThreads.emptyTitle')}
                description={t('qaThreads.emptyDesc')}
              />
            )
          ) : (
            <ThreadList
              threads={filteredThreads}
              onSelectThread={handleSelectThread}
            />
          )}
        </>
      )}
    </div>
  );
}

export default CourseQA;
